import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Bell, ExternalLink, Loader2, RefreshCw } from "lucide-react";

const FILTERS = ["All", "CQC", "Ofsted"];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function RegulatoryUpdates() {
  const [data, setData] = useState({ updates: [], last_refresh: null });
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = (reg) => {
    setLoading(true);
    const q = reg && reg !== "All" ? `?regulator=${reg}` : "";
    api.get(`/regulatory-updates${q}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-2xl mb-8">
        <span className="overline text-accent">Live Feed</span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 mb-3">
          Regulatory updates
        </h1>
        <p className="text-muted-foreground text-lg">
          Automatic updates on changes to regulations and guidance, pulled directly from CQC and Ofsted.
        </p>
        {data.last_refresh && (
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> Last synced {new Date(data.last_refresh).toLocaleString("en-GB")}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`updates-filter-${f.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3">
          {data.updates.map((u, i) => (
            <a key={u.url || u.title} href={u.url} target="_blank" rel="noopener noreferrer"
              data-testid={`regulatory-update-${i}`}
              className="block bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-primary/40 transition-all group">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                  u.regulator === "CQC" ? "bg-emerald-500/10" : "bg-blue-500/10"}`}>
                  <Bell className={`w-5 h-5 ${u.regulator === "CQC" ? "text-emerald-600 dark:text-emerald-400" : "text-blue-600 dark:text-blue-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Badge variant="secondary" className={u.regulator === "CQC"
                      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0"
                      : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-0"}>{u.regulator}</Badge>
                    {u.doc_type && <span className="text-xs text-muted-foreground capitalize">{u.doc_type}</span>}
                    <span className="text-xs text-muted-foreground ml-auto">{timeAgo(u.published_at)}</span>
                  </div>
                  <h3 className="font-semibold leading-snug group-hover:text-primary transition-colors flex items-start gap-1.5">
                    {u.title}
                    <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  {u.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{u.description}</p>}
                </div>
              </div>
            </a>
          ))}
          {!data.updates.length && (
            <p className="text-center text-muted-foreground py-12">No updates available right now.</p>
          )}
        </div>
      )}
    </div>
  );
}
